import type { EmbedPage } from "@/features/supabase";
import { formatGeographicResolution } from "@/utils/admin/geographicResolution";
import { Tag, Tooltip } from "antd";
import classNames from "classnames";
import { getDatasetBlurb, getDatasetMetricLabels } from "../functions";
import { DatasetAvailabilityTag } from "./DatasetAvailabilityTag";
interface DatasetCardProps {
  dataset: EmbedPage;
  isSelected: boolean;
  onSelect: (dataset: EmbedPage) => void;
}

/** A labelled row of tags, hidden entirely when there is nothing to show. */
const TagRow = ({
  label,
  values
}: {
  label: string;
  values: string[];
}) => {
  if (!values.length) return null;
  return <div className="flex flex-wrap items-center gap-1" data-sentry-component="TagRow" data-sentry-source-file="DatasetCard.tsx">
      <span className="text-xs text-gray-500">{label}</span>
      {values.map(value => <Tag key={value} className="m-0 text-xs">
          {value}
        </Tag>)}
    </div>;
};
export const DatasetCard = ({
  dataset,
  isSelected,
  onSelect
}: DatasetCardProps) => {
  const blurb = getDatasetBlurb(dataset);
  const metrics = getDatasetMetricLabels(dataset.metrics);
  const resolution = dataset.geographic_resolution ? formatGeographicResolution(dataset.geographic_resolution) : "";
  const isDisabled = !dataset.can_open;
  const card = <div role="button" aria-disabled={isDisabled} tabIndex={isDisabled ? -1 : 0} onClick={() => !isDisabled && onSelect(dataset)} onKeyDown={e => {
    if (isDisabled) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onSelect(dataset);
    }
  }} className={classNames("flex h-full flex-col gap-2 rounded-xl border p-4 transition-all", isDisabled ? "cursor-not-allowed border-gray-200 bg-subtle opacity-70" : "cursor-pointer hover:shadow-sm active:scale-[0.995]", !isDisabled && !isSelected && "border-gray-200 hover:border-gray-400", !isDisabled && isSelected && "border-accent bg-accent/5 shadow-sm ring-1 ring-accent")}>
      <div className="flex items-start justify-between gap-2">
        <span className="min-w-0 font-medium text-gray-900" title={dataset.title}>
          {dataset.title}
        </span>
        <div className="flex shrink-0 items-center gap-1">
          {/*{isSelected && !isDisabled && <Tag className="m-0 border-accent text-accent">Selected</Tag>}*/}
          <DatasetAvailabilityTag canOpen={dataset.can_open} />
        </div>
      </div>

      {blurb && <p className="m-0 line-clamp-3 text-sm text-gray-500" title={blurb}>
          {blurb}
        </p>}

      {/* Pushed to the bottom so cards of differing blurb length stay aligned. */}
      <div className="mt-auto flex flex-col gap-1 pt-1">
        <TagRow label="Metrics" values={metrics} />
        <TagRow label="Coverage" values={resolution ? [resolution] : []} />
      </div>
    </div>;
  return isDisabled ? <Tooltip title="Contact your Customer Success Manager to request access." data-sentry-element="Tooltip" data-sentry-component="DatasetCard" data-sentry-source-file="DatasetCard.tsx">{card}</Tooltip> : card;
};